<template>
  <div class="intellectual-property-detail">
    <intellectual-property-detail-edit
      v-if="editable"
      :edit-form-data="editFormData"
      :error="error"
      :has-error-in-page="hasErrorInPage"
      :has-save-i-p-errors="hasSaveIPErrors"
      :uuid="uuid"
      :subject-list="subjectList"
      :level-list="levelList"
      :owner-group="ownerGroup"
      :hide-uuid="hideUuid"
      :readonly-resource-type="readonlyResourceType"
      @ip-content-change="handleIpContentChange"
      @ip-error-status="handleIpErrorStatus"
    />
    <intellectual-property-detail-view
      v-else
      :edit-form-data="editFormData"
      :uuid="uuid"
      :owner-group="ownerGroup"
    />
  </div>
</template>

<script setup lang="ts">
import IntellectualPropertyDetailView from "@modules/manage-resource/components/IntellectualPropertyDetailView.vue";
import IntellectualPropertyDetailEdit from "@modules/manage-resource/components/IntellectualPropertyDetailEdit.vue";
import { MediaObjectFormData } from "@plugins/media/types.ts";
import { LevelAndStream } from "@modules/level-and-stream/types";
import { Subject } from "@modules/student-group/student-group-types";

// Emits
const emit = defineEmits<{
  (e: "ipContentChange", payload): void;
  (e: "ipErrorStatus", payload): void;
}>();

// Props
interface Props {
  uuid: string;
  editFormData: MediaObjectFormData;
  error: Record<string, string>;
  hasErrorInPage: boolean;
  hasSaveIPErrors: boolean;
  editable: boolean;
  subjectList: Subject[];
  levelList: LevelAndStream[];
  ownerGroup: string;
  hideUuid?: boolean;
  readonlyResourceType?: boolean;
}

withDefaults(defineProps<Props>(), {
  hideUuid: false,
  readonlyResourceType: false,
});

// Methods
const handleIpContentChange = (value) => {
  emit("ipContentChange", value);
};

const handleIpErrorStatus = (value) => {
  emit("ipErrorStatus", value);
};
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.intellectual-property-detail {
  &.edit {
    .license-duration {
      gap: $spacing-05;

      .cv-date-picker {
        &.disabled {
          .bx--date-picker__icon {
            pointer-events: none;
            [stroke] {
              stroke: $disabled-02;
            }
          }
        }
      }
    }
  }
  &.view {
    .license-duration {
      gap: $spacing-12;
    }
  }
  .bx--form {
    :is(.field-set, .field-input) {
      margin-bottom: 0;
    }
  }

  .display-restriction {
    .text {
      @include carbon--type-style("body-short-01");
    }
  }

  .field-group {
    display: flex;
    flex-flow: column nowrap;
    gap: $spacing-05;

    &:not(:last-of-type) {
      margin-bottom: $spacing-07;
      padding-bottom: $spacing-07;
      border-bottom: $divider-02;
    }

    &.metadata {
      .input-checkbox {
        margin: $spacing-03 0 0;
      }
    }
  }

  .license-duration {
    display: flex;
    flex-flow: row wrap;

    > .field-set {
      flex: 1;

      :is(
        .bx--date-picker,
        .bx--date-picker-container,
        .bx--date-picker-input__wrapper,
        .bx--date-picker__input
      ) {
        width: 100%;
      }

      .bx--date-picker-container {
        align-items: flex-start;
      }

      @include media("tablet-min") {
        flex: 0 0 auto;

        :is(
          .bx--date-picker,
          .bx--date-picker-container,
          .bx--date-picker-input__wrapper,
          .bx--date-picker__input
        ) {
          width: auto;
        }
      }
    }
  }

  .resource-type-description {
    @include carbon--type-style("helper-text-01", true);
    color: $text-02;
  }
}
</style>
